import {
  ChevronsUpDown,
  ClipboardCheck,
  ClipboardList,
  FileEdit,
  Handshake,
  LayoutDashboard,
  LogOut,
  Building2,
  Boxes,
  Factory,
  Settings,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/features/auth/hooks/use-auth"
import { useMe } from "@/features/auth/hooks/use-me"
import { Skeleton } from "@/components/ui/skeleton"
import logoIndigo from "@/assets/logo-indigo.png"
import logoSingleA from "@/assets/logo-single-a.png"
import { paths } from "@/routes/paths"

type SectionKey = "overview" | "procurement" | "factory" | "export" | "system"

type ItemKey =
  | "dashboard"
  | "contracts"
  | "orders"
  | "reception"
  | "millFloor"
  | "lots"
  | "transitOrders"
  | "documentDelivery"
  | "clients"
  | "cooperatives"
  | "usersRoles"

interface NavItem {
  key: ItemKey
  to: string
  icon: typeof LayoutDashboard
  badge?: number
  end?: boolean
}

interface NavSection {
  key: SectionKey
  items: NavItem[]
}

const sections: NavSection[] = [
  {
    key: "overview",
    items: [
      {
        key: "dashboard",
        to: paths.dashboard,
        icon: LayoutDashboard,
        end: true,
      },
    ],
  },
  {
    key: "procurement",
    items: [
      { key: "contracts", to: paths.contracts, icon: Handshake },
      { key: "orders", to: paths.orders, icon: ClipboardList },
    ],
  },
  {
    key: "factory",
    items: [
      { key: "reception", to: paths.reception, icon: ClipboardCheck },
      { key: "millFloor", to: paths.millFloor, icon: Factory },
      { key: "lots", to: paths.lots, icon: Boxes },
    ],
  },
  {
    key: "export",
    items: [{ key: "transitOrders", to: paths.transitOrders, icon: FileEdit }],
  },
  {
    key: "system",
    items: [
      // { key: "clients", to: paths.clients, icon: Globe },
      { key: "cooperatives", to: paths.cooperatives, icon: Building2 },
    ],
  },
]

function isActiveRoute(currentPath: string, target: string, end = false) {
  if (end) {
    return currentPath === target
  }
  return currentPath === target || currentPath.startsWith(`${target}/`)
}

export function AppSidebar() {
  const { pathname } = useLocation()
  const { t } = useTranslation(["nav", "common"])
  const { signOut } = useAuth()
  const { data: me, isLoading: isLoadingMe } = useMe()
  const navigate = useNavigate()

  const handleLogout = () => {
    signOut()
    navigate(paths.login, { replace: true })
  }

  const fullName = [me?.firstName, me?.lastName]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(" ")
    .trim()
  const displayName = fullName || me?.email || ""
  const initials =
    (me?.firstName?.[0] ?? "") + (me?.lastName?.[0] ?? "") ||
    me?.email?.[0]?.toUpperCase() ||
    "?"

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center overflow-hidden border-b border-sidebar-border px-3 group-data-[collapsible=icon]:px-0">
        <Link
          to={paths.dashboard}
          aria-label="Abeya"
          className="flex h-full items-center group-data-[collapsible=icon]:justify-center"
        >
          <img
            src={logoIndigo}
            alt="Abeya"
            className="h-6 w-auto group-data-[collapsible=icon]:hidden dark:brightness-0 dark:invert"
          />
          <img
            src={logoSingleA}
            alt="Abeya"
            aria-hidden
            className="hidden h-6 w-auto group-data-[collapsible=icon]:block dark:brightness-0 dark:invert"
          />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {sections.map((section) => (
          <SidebarGroup key={section.key}>
            <SidebarGroupLabel className="text-xs font-medium tracking-wider text-muted-foreground/70 uppercase">
              {t(`sections.${section.key}`)}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const active = isActiveRoute(pathname, item.to, item.end)
                  const label = t(`items.${item.key}`)
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={label}
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span>{label}</span>
                        </Link>
                      </SidebarMenuButton>
                      {item.badge !== undefined && (
                        <SidebarMenuBadge>{item.badge}</SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("account.menu", { ns: "common" })}
              className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-1 hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-none"
            >
              <Avatar className="size-9 shrink-0">
                <AvatarFallback className="bg-sidebar-primary text-sm font-medium text-sidebar-primary-foreground uppercase">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-1 flex-col overflow-hidden text-sm group-data-[collapsible=icon]:hidden">
                {isLoadingMe ? (
                  <>
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="mt-1 h-3 w-36" />
                  </>
                ) : (
                  <>
                    <span className="truncate font-medium">{displayName}</span>
                    {me?.email && fullName ? (
                      <span className="truncate text-xs text-muted-foreground">
                        {me.email}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
              <ChevronsUpDown className="size-4 text-muted-foreground group-data-[collapsible=icon]:hidden" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="top"
            className="w-56"
            sideOffset={8}
          >
            <DropdownMenuLabel className="flex flex-col">
              {isLoadingMe ? (
                <>
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="mt-1 h-3 w-36" />
                </>
              ) : (
                <>
                  <span className="font-medium">{displayName}</span>
                  {me?.email && fullName ? (
                    <span className="text-xs font-normal text-muted-foreground">
                      {me.email}
                    </span>
                  ) : null}
                </>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Settings />
              {t("account.settings", { ns: "common" })}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              {t("account.logout", { ns: "common" })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
